import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Edit2, Trash2, Plus } from 'lucide-react';
import {
    get_customer_profile,
    update_customer_profile,
    change_customer_password,
    messageClear,
} from '../../store/reducers/dashboardReducer';

const inputCls =
    'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all';

const labelCls = 'block text-xs font-medium text-gray-500 mb-1';

const CardSection = ({ title, children }) => (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{title}</span>
        </div>
        <div className="p-4">{children}</div>
    </div>
);

const PROFILE_FIELDS = [
    { key: 'name',  label: 'Full Name',     type: 'text' },
    { key: 'email', label: 'Email Address', type: 'email' },
    { key: 'phone', label: 'Phone Number',  type: 'tel' },
];

const ADDRESS_FIELDS = [
    { field: 'name',     placeholder: 'Full Name' },
    { field: 'phone',    placeholder: 'Phone Number' },
    { field: 'address',  placeholder: 'Street Address', full: true },
    { field: 'area',     placeholder: 'Area / District' },
    { field: 'city',     placeholder: 'City' },
    { field: 'province', placeholder: 'Province / Emirate' },
    { field: 'post',     placeholder: 'Post Code' },
];

const MyProfile = () => {
    const dispatch = useDispatch();
    const { profile, successMessage } = useSelector(s => s.dashboard);

    const [editField, setEditField]   = useState(null);
    const [form, setForm]             = useState({ name: '', email: '', phone: '' });
    const [addresses, setAddresses]   = useState([]);
    const [editingIdx, setEditingIdx] = useState(null);
    const [passwords, setPasswords]   = useState({ oldPassword: '', newPassword: '' });

    useEffect(() => { dispatch(get_customer_profile()); }, [dispatch]);

    useEffect(() => {
        if (profile) {
            setForm({ name: profile.name || '', email: profile.email || '', phone: profile.phone || '' });
            setAddresses(profile.addresses || []);
        }
    }, [profile]);

    useEffect(() => {
        if (successMessage) { toast.success(successMessage); dispatch(messageClear()); }
    }, [successMessage, dispatch]);

    const saveProfile = () => {
        dispatch(update_customer_profile({ ...form, addresses }));
        setEditField(null);
    };

    const onAddrChange = (idx, field, val) => {
        const copy = [...addresses];
        copy[idx] = { ...copy[idx], [field]: val };
        setAddresses(copy);
    };

    const addAddress = () => {
        setAddresses([...addresses, { name: '', phone: '', address: '', area: '', city: '', province: '', post: '' }]);
        setEditingIdx(addresses.length);
    };

    const saveAddress = () => {
        dispatch(update_customer_profile({ ...form, addresses }));
        setEditingIdx(null);
    };

    const deleteAddress = (idx) => {
        const updated = addresses.filter((_, i) => i !== idx);
        setAddresses(updated);
        dispatch(update_customer_profile({ ...form, addresses: updated }));
    };

    const changePassword = (e) => {
        e.preventDefault();
        dispatch(change_customer_password(passwords));
        setPasswords({ oldPassword: '', newPassword: '' });
    };

    return (
        <div className="space-y-3">
            <div className="flex md:flex-col gap-3 items-start">
                {/* Left — grows to fill available space */}
                <div className="flex-1 min-w-0 space-y-3">
                    {/* Personal info */}
                    <CardSection title="Personal Information">
                        <div className="space-y-3">
                            {PROFILE_FIELDS.map(({ key, label, type }) => (
                                <div key={key}>
                                    <label className={labelCls}>{label}</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type={type}
                                            value={form[key]}
                                            disabled={editField !== key}
                                            onChange={e => setForm({ ...form, [key]: e.target.value })}
                                            className={`${inputCls} flex-1 ${editField !== key ? 'bg-gray-50 cursor-default' : ''}`}
                                        />
                                        <button
                                            onClick={() => setEditField(editField === key ? null : key)}
                                            className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-colors"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {editField && (
                                <div className="flex gap-2 pt-1">
                                    <button
                                        onClick={saveProfile}
                                        className="px-4 py-2 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-full transition-colors"
                                    >
                                        Save Changes
                                    </button>
                                    <button
                                        onClick={() => setEditField(null)}
                                        className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    </CardSection>

                    {/* Addresses */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                Saved Addresses
                            </span>
                            <button
                                onClick={addAddress}
                                className="flex items-center gap-1 text-xs font-semibold text-orange-500 hover:text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-1.5 rounded-full transition-colors"
                            >
                                <Plus className="w-3 h-3" /> Add
                            </button>
                        </div>

                        {addresses.length === 0 ? (
                            <div className="flex flex-col items-center py-10 text-center">
                                <p className="text-sm text-gray-500">No addresses saved</p>
                                <p className="text-xs text-gray-400 mt-1">Add an address to speed up checkout</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {addresses.map((addr, i) => (
                                    <div key={i} className="p-4">
                                        {editingIdx === i ? (
                                            <div className="grid grid-cols-2 sm:grid-cols-1 gap-3">
                                                {ADDRESS_FIELDS.map(({ field, placeholder, full }) => (
                                                    <input
                                                        key={field}
                                                        className={`${inputCls} ${full ? 'col-span-2 sm:col-span-1' : ''}`}
                                                        placeholder={placeholder}
                                                        value={addr[field] || ''}
                                                        onChange={e => onAddrChange(i, field, e.target.value)}
                                                    />
                                                ))}
                                                <div className="col-span-2 sm:col-span-1 flex gap-2">
                                                    <button
                                                        onClick={saveAddress}
                                                        className="px-4 py-2 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-full transition-colors"
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingIdx(null)}
                                                        className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-start justify-between gap-4">
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">{addr.name}</p>
                                                    <p className="text-xs text-gray-600 mt-0.5">{addr.address}</p>
                                                    {addr.area && <p className="text-xs text-gray-500">{addr.area}</p>}
                                                    <p className="text-xs text-gray-500">
                                                        {[addr.city, addr.province].filter(Boolean).join(', ')}
                                                        {addr.post ? ` - ${addr.post}` : ''}
                                                    </p>
                                                    {addr.phone && <p className="text-xs text-gray-400 mt-1">{addr.phone}</p>}
                                                </div>
                                                <div className="flex gap-1 flex-shrink-0">
                                                    <button
                                                        onClick={() => setEditingIdx(i)}
                                                        className="p-1.5 rounded text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-colors"
                                                    >
                                                        <Edit2 className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteAddress(i)}
                                                        className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right — password, fixed width so left side gets all remaining space */}
                <div className="w-[260px] flex-shrink-0 md:w-full">
                    <CardSection title="Update Password">
                        <form onSubmit={changePassword} className="space-y-3">
                            <div>
                                <label className={labelCls}>Current Password</label>
                                <input
                                    type="password"
                                    placeholder="Enter current password"
                                    value={passwords.oldPassword}
                                    onChange={e => setPasswords({ ...passwords, oldPassword: e.target.value })}
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>New Password</label>
                                <input
                                    type="password"
                                    placeholder="Enter new password"
                                    value={passwords.newPassword}
                                    onChange={e => setPasswords({ ...passwords, newPassword: e.target.value })}
                                    className={inputCls}
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full py-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-full transition-colors"
                            >
                                Update Password
                            </button>
                        </form>
                    </CardSection>
                </div>
            </div>
        </div>
    );
};

export default MyProfile;
